import MarkdownIt from "markdown-it";
import type Token from "markdown-it/lib/token.mjs";
import footnote from "markdown-it-footnote";
import taskLists from "markdown-it-task-lists";
import {
  defaultMarkdownSerializer,
  MarkdownParser,
  MarkdownSerializer,
  type MarkdownSerializerState,
  type ParseSpec,
} from "prosemirror-markdown";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { Fragment } from "prosemirror-model";

import { focusSchema } from "../schema";
import { hydratePlainTextMentions } from "./mentions";

const listIsTight = (tokens: readonly Token[], index: number) => {
  while (++index < tokens.length) {
    if (tokens[index]?.type !== "list_item_open") {
      return Boolean(tokens[index]?.hidden);
    }
  }

  return false;
};

const getTaskCheckedState = (tokens: readonly Token[], index: number) => {
  for (let nextIndex = index + 1; nextIndex < tokens.length; nextIndex++) {
    const token = tokens[nextIndex];

    if (!token || token.type === "list_item_close") {
      break;
    }

    if (token.type !== "inline") {
      continue;
    }

    const checkboxToken = token.children?.find(
      (child) => child.type === "html_inline",
    );

    if (!checkboxToken?.content.includes("task-list-item-checkbox")) {
      return null;
    }

    return checkboxToken.content.includes("checked");
  }

  return null;
};

const markdownIt = MarkdownIt({
  html: false,
  linkify: false,
  typographer: false,
})
  .enable(["table", "strikethrough"])
  .use(footnote)
  .use(taskLists);

const parserTokens: Record<string, ParseSpec> = {
  blockquote: { block: "blockquote" },
  bullet_list: {
    block: "bullet_list",
    getAttrs: (_token, tokens, index) => ({
      tight: listIsTight(tokens, index),
    }),
  },
  code_block: { block: "code_block", noCloseToken: true },
  fence: {
    block: "code_block",
    getAttrs: (token) => ({
      language: token.info?.trim() || "",
    }),
    noCloseToken: true,
  },
  footnote_anchor: { ignore: true, noCloseToken: true },
  footnote_block: { ignore: true },
  footnote: {
    block: "footnote_definition",
    getAttrs: (token) => ({
      label: token.meta?.label ?? String(token.meta?.id ?? ""),
    }),
  },
  footnote_ref: {
    node: "footnote_reference",
    getAttrs: (token) => ({
      label: token.meta?.label ?? String(token.meta?.id ?? ""),
    }),
  },
  hardbreak: { node: "hard_break" },
  heading: {
    block: "heading",
    getAttrs: (token) => ({
      level: Number(token.tag.slice(1)) || 1,
    }),
  },
  hr: { node: "horizontal_rule" },
  html_inline: { ignore: true, noCloseToken: true },
  image: {
    node: "image",
    getAttrs: (token) => ({
      alt: token.children?.[0]?.content || null,
      src: token.attrGet("src"),
      title: token.attrGet("title") || null,
    }),
  },
  inline: { ignore: true },
  link: {
    getAttrs: (token) => ({
      href: token.attrGet("href"),
      title: token.attrGet("title") || null,
    }),
    mark: "link",
  },
  list_item: {
    block: "list_item",
    getAttrs: (_token, tokens, index) => ({
      checked: getTaskCheckedState(tokens, index),
    }),
  },
  ordered_list: {
    block: "ordered_list",
    getAttrs: (token, tokens, index) => ({
      order: Number(token.attrGet("start")) || 1,
      tight: listIsTight(tokens, index),
    }),
  },
  paragraph: { block: "paragraph" },
  s: { mark: "strike" },
  table: { block: "table" },
  tbody: { ignore: true },
  td: { block: "table_cell" },
  th: { block: "table_header" },
  thead: { ignore: true },
  tr: { block: "table_row" },

  code_inline: { mark: "code", noCloseToken: true },
  em: { mark: "em" },
  strong: { mark: "strong" },
};

const parser = new MarkdownParser(focusSchema, markdownIt, parserTokens);

const tableCellText = (cell: ProseMirrorNode) =>
  cell.textContent.replace(/\|/g, "\\|").trim();

const renderTableRow = (row: ProseMirrorNode) => {
  const cells: string[] = [];

  row.forEach((cell) => {
    cells.push(tableCellText(cell));
  });

  return `| ${cells.join(" | ")} |`;
};

const serializerNodes = {
  ...defaultMarkdownSerializer.nodes,

  bullet_list(state: MarkdownSerializerState, node: ProseMirrorNode) {
    state.renderList(node, "  ", () => "- ");
  },

  code_block(state: MarkdownSerializerState, node: ProseMirrorNode) {
    const backticks = node.textContent.match(/`{3,}/gm);
    const fence = backticks ? `${backticks.sort().slice(-1)[0]}\`` : "```";
    const language = node.attrs.language || node.attrs.params || "";

    state.write(`${fence}${language}\n`);
    state.text(node.textContent, false);
    state.write("\n");
    state.write(fence);
    state.closeBlock(node);
  },

  footnote_definition(state: MarkdownSerializerState, node: ProseMirrorNode) {
    state.write(`[^${node.attrs.label}]: `);
    state.text(node.textContent, false);
    state.closeBlock(node);
  },

  footnote_reference(state: MarkdownSerializerState, node: ProseMirrorNode) {
    state.write(`[^${node.attrs.label}]`);
  },

  list_item(state: MarkdownSerializerState, node: ProseMirrorNode) {
    if (typeof node.attrs.checked === "boolean") {
      state.write(`[${node.attrs.checked ? "x" : " "}] `);
    }

    state.renderContent(node);
  },

  mention(state: MarkdownSerializerState, node: ProseMirrorNode) {
    state.write(`${node.attrs.trigger || "@"}${node.attrs.username}`);
  },

  table(state: MarkdownSerializerState, node: ProseMirrorNode) {
    const rows: ProseMirrorNode[] = [];
    node.forEach((row) => rows.push(row));

    if (!rows.length) {
      return;
    }

    state.ensureNewLine();
    state.write(renderTableRow(rows[0]!));
    state.ensureNewLine();
    state.write(
      `| ${Array.from({ length: rows[0]!.childCount }, () => "---").join(
        " | ",
      )} |`,
    );

    rows.slice(1).forEach((row) => {
      state.ensureNewLine();
      state.write(renderTableRow(row));
    });

    state.closeBlock(node);
  },

  table_cell() {},
  table_header() {},
  table_row() {},
};

const serializer = new MarkdownSerializer(serializerNodes, {
  ...defaultMarkdownSerializer.marks,
  strike: {
    close: "~~",
    mixable: true,
    open: "~~",
  },
});

const trimTaskListLeadingSpace = (node: ProseMirrorNode): ProseMirrorNode => {
  if (
    node.type.name === "list_item" &&
    typeof node.attrs.checked === "boolean" &&
    node.firstChild?.type.name === "paragraph" &&
    node.firstChild.firstChild?.isText &&
    node.firstChild.firstChild.text?.startsWith(" ")
  ) {
    const paragraph = node.firstChild;
    const paragraphChildren: ProseMirrorNode[] = [];

    paragraph.forEach((child, _offset, index) => {
      if (index === 0 && child.isText && child.text) {
        paragraphChildren.push(
          focusSchema.text(child.text.slice(1), child.marks),
        );
        return;
      }

      paragraphChildren.push(child);
    });

    const listItemChildren: ProseMirrorNode[] = [
      paragraph.type.create(
        paragraph.attrs,
        Fragment.fromArray(paragraphChildren),
        paragraph.marks,
      ),
    ];

    for (let index = 1; index < node.childCount; index++) {
      listItemChildren.push(trimTaskListLeadingSpace(node.child(index)));
    }

    return node.type.create(
      node.attrs,
      Fragment.fromArray(listItemChildren),
      node.marks,
    );
  }

  if (node.isLeaf) {
    return node;
  }

  const children: ProseMirrorNode[] = [];
  node.forEach((child) => {
    children.push(trimTaskListLeadingSpace(child));
  });

  return node.type.create(node.attrs, Fragment.fromArray(children), node.marks);
};

export const parseMarkdownToDoc = (markdown: string) => {
  return hydratePlainTextMentions(
    trimTaskListLeadingSpace(parser.parse(markdown || "")),
  );
};

export const serializeDocToMarkdown = (doc: ProseMirrorNode) => {
  return serializer.serialize(doc).trim();
};

export const markdownParser = parser;
export const markdownSerializer = serializer;
